/** What a stack or a hex IS, in the numbers the battle is actually using.
 *
 * The rules never look here. Every figure on a card is either read straight off
 * `BattleState` or produced by the kernel's own helper - `resolveStat` for the
 * stat breakdowns, `magicResistPct` for resistance - so a number the player
 * reads is the number the next blow will use. Nothing in this file re-derives a
 * rule; where the arithmetic was only inside the kernel, the kernel grew an
 * export rather than this file growing a second copy.
 *
 * THE ONE THING THAT IS NOT A MODIFIER. A hero's Attack and Defence points are
 * folded into a stack's base at `initBattle` (`def.attack + heroAttack`), not
 * carried as modifiers, so `resolveStat` cannot attribute them and a card built
 * only from terms would show a Dwarf with 9 attack and no reason for it. The
 * hero's share is recovered by subtracting the creature's own definition from
 * the stack's base - the one derivation here, and it is arithmetic on two
 * numbers the state already holds rather than a rule.
 */

import type {
  BattleState, ContentRegistry, CreatureDefId, Modifier, ModifierSrcId, StatKey,
} from "@game/core";
import {
  MAGIC_SRC_PREFIX, SRC_START_COUNT, elementalWards, hexCol, hexRow,
  magicResistPct, resolveStat,
} from "@game/core";

import type { InfoCard, InfoRow } from "../adventure/info.js";
import { abilityPhrases } from "../screens/creatureCard.js";
import { titleCase } from "../screens/format.js";

/** The cap `magicStrikeFp` clamps a target's total ward to, as a percentage. */
const RESIST_DISPLAY_CAP = 100;

const signed = (n: number): string => (n > 0 ? `+${String(n)}` : String(n));

/**
 * A modifier source in words.
 *
 * Source ids are dotted namespaces (`spell.bless`, `ability.petrify`,
 * `battle.defend`). A spell's id is looked up in content so the card says
 * "Bless" and not "Bless" only by luck of the id spelling; everything else is
 * its last segment in title case, which is why a source added tomorrow reads
 * sensibly here without this map being touched.
 */
export function sourceLabel(content: ContentRegistry, src: ModifierSrcId): string {
  const id = src as string;
  if (id.startsWith("spell.")) {
    const key = id.slice("spell.".length);
    const named = content.spells.get(key as never);
    return named?.name ?? titleCase(key);
  }
  const known: Readonly<Record<string, string>> = {
    "battle.defend": "defending",
    "battle.attack_diff": "attack vs defence",
    "battle.defense_diff": "attack vs defence",
    "battle.range_penalty": "range penalty",
    "battle.melee_penalty": "melee penalty",
    "battle.wall_penalty": "wall penalty",
    "battle.luck": "luck",
    "battle.cover": "cover",
  };
  const hit = known[id];
  if (hit !== undefined) return hit;
  const last = id.slice(id.lastIndexOf(".") + 1);
  return titleCase(last.replace(/_/g, " "));
}

const modsForStat = (mods: readonly Modifier[], stat: StatKey): readonly Modifier[] =>
  mods.filter((m) => m.stat === stat);

/**
 * One stat as "9 = 6 base +3 hero +2 Bless".
 *
 * The parts are only spelled out when there is more than one of them: a Spearwarden
 * with no hero and no enchantment should read "4", not "4 = 4 base".
 */
function statValue(
  content: ContentRegistry,
  base: number,
  heroShare: number,
  mods: readonly Modifier[],
  stat: StatKey,
): string {
  const withHero = base + heroShare;
  const r = resolveStat(withHero, modsForStat(mods, stat));
  const final = r.value < 0 ? 0 : r.value;
  const parts: string[] = [`${String(base)} base`];
  if (heroShare !== 0) parts.push(`${signed(heroShare)} hero`);
  for (const t of r.terms) {
    if (!t.applied) continue;
    const label = sourceLabel(content, t.source);
    if (t.op === "add") parts.push(`${signed(t.value)} ${label}`);
    else if (t.op === "mulFp") parts.push(`x${(t.value / 1000).toFixed(2)} ${label}`);
    else parts.push(`${t.op} ${String(t.value)} ${label}`);
  }
  return parts.length < 2 ? String(final) : `${String(final)} = ${parts.join(" ")}`;
}

/**
 * Modifiers that are records rather than effects.
 *
 * Two of them ride on every stack in the game: the hero's magic profile
 * (`hero.magic.*` - wisdom, mastery, sorcery, carried on each stack so a cast
 * can read it) and the battle-start count the Revenge ability compares against.
 * Neither is something on THIS stack that a player could dispel or plan around,
 * and listing them put "Under Wisdom." and "Under Start Count." on every card.
 *
 * A blacklist rather than a whitelist of visible stats, deliberately: a
 * bookkeeping namespace that is added later and missed here shows one stray
 * line, while a real enchantment missed by a whitelist would be hidden - and
 * hidden is the failure a player cannot see.
 */
function isBookkeeping(src: ModifierSrcId): boolean {
  const id = src as string;
  return id.startsWith(MAGIC_SRC_PREFIX) || id === (SRC_START_COUNT as string);
}

/**
 * Every enchantment and ability currently on the stack, one line each.
 *
 * Deliberately NOT filtered against the stat rows above, even though a Bless
 * that adds to attack appears in both. The rows answer "why is this number
 * what it is"; this list answers "what is on this stack", which is the question
 * a player asks before deciding whether to Dispel - and a list that hid every
 * effect already visible in a row would be a list that went empty exactly when
 * the stack was most enchanted.
 */
function effectLines(content: ContentRegistry, mods: readonly Modifier[]): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of mods) {
    if (isBookkeeping(m.source)) continue;
    const label = sourceLabel(content, m.source);
    if (seen.has(label)) continue;
    seen.add(label);
    out.push(`Under ${label}.`);
  }
  return out;
}

/**
 * Everything the battle knows about one stack.
 *
 * `slot` is an index into `state.stacks`; a slot past the end returns an empty
 * card rather than throwing, because the caller is a pointer and a pointer can
 * outlive the stack it was over.
 */
export function battleStackCard(
  state: BattleState, content: ContentRegistry, slot: number,
): InfoCard {
  const st = state.stacks;
  if (slot < 0 || slot >= st.n) {
    return { title: "", subtitle: null, icon: null, lines: [], rows: [], footer: null };
  }
  const defId = st.def[slot]! as CreatureDefId;
  const def = content.creatures.get(defId);
  if (def === undefined) {
    return { title: "", subtitle: null, icon: null, lines: [], rows: [], footer: null };
  }
  const count = st.count[slot]!;
  const mods = st.mods[slot]!;

  // The hero's folded-in share; see the file header.
  const heroAttack = st.attack[slot]! - def.attack;
  const heroDefense = st.defense[slot]! - def.defense;

  const rows: InfoRow[] = [
    { label: "Attack", value: statValue(content, def.attack, heroAttack, mods, "attack" as StatKey) },
    { label: "Defence", value: statValue(content, def.defense, heroDefense, mods, "defense" as StatKey) },
    { label: "Damage", value: `${String(st.damageMin[slot]!)} - ${String(st.damageMax[slot]!)}` },
    { label: "Health", value: `${String(st.topHp[slot]!)} / ${String(st.maxHp[slot]!)}` },
    { label: "Speed", value: String(st.speed[slot]!) },
    { label: "Morale", value: signed(st.morale[slot]!) },
    { label: "Luck", value: signed(st.luck[slot]!) },
  ];
  if (st.shots[slot]! > 0) rows.push({ label: "Shots", value: String(st.shots[slot]!) });
  if (st.casts[slot]! > 0) rows.push({ label: "Casts left", value: String(st.casts[slot]!) });
  if (st.strikes[slot]! > 0) rows.push({ label: "Magic blows", value: String(st.strikes[slot]!) });

  // Elemental enchantment, both directions. An Enchant Weapon spell writes a
  // STRIKE stat and a Protection spell writes a RESIST one; neither is `attack`
  // or `defense`, so without these rows a Fire Sword on a Dwarf showed up as
  // "Under Fire Sword" with no number anywhere - the spell named but its whole
  // effect invisible. Both arrive as percentages; see `elementalWards`.
  for (const w of elementalWards(state, slot)) {
    const school = titleCase(w.school as string);
    if (w.strikePct !== 0) {
      rows.push({ label: `${school} blows`, value: `${signed(w.strikePct)}% damage` });
    }
    if (w.protectionPct !== 0) {
      rows.push({ label: `${school} protection`, value: `${String(w.protectionPct)}%` });
    }
  }

  // The kernel's own figure, uncapped, with the cap named beside it when it
  // bites - a Ironbeard under Resistance and a Protection spell is over 100
  // and a card that printed the raw sum would promise an immunity the rule
  // does not give.
  const resist = magicResistPct(state, content, slot);
  if (resist > 0) {
    rows.push({
      label: "Magic resistance",
      value: resist > RESIST_DISPLAY_CAP
        ? `${String(RESIST_DISPLAY_CAP)}% (${String(resist)}% before the cap)`
        : `${String(resist)}%`,
    });
  }

  const side = st.side[slot]! === 0 ? "Attacker" : "Defender";
  const acting = state.order[state.turnIndex] === slot;

  return {
    title: count === 1 ? def.name : def.namePlural,
    subtitle: `${String(count)} in stack - ${side}${acting ? ", acting now" : ""}`,
    icon: null,
    lines: [...abilityPhrases(content, def), ...effectLines(content, mods)],
    rows,
    footer: null,
  };
}

/** The terrain kinds that carry a sentence of their own. */
const TERRAIN_NOTE: Readonly<Record<string, string>> = Object.freeze({
  lava: "Scorched ground: nothing grows here and there is no cover.",
  swamp: "Boggy ground underfoot.",
  snow: "Snow lies over the field.",
  sand: "Loose sand across the whole field.",
});

/**
 * Everything the battle knows about one hex: what the ground is, and whatever
 * is standing on or soaked into it.
 */
export function battleHexCard(
  state: BattleState, content: ContentRegistry, hex: number,
): InfoCard {
  const lines: string[] = [];
  const note = TERRAIN_NOTE[state.terrain];
  if (note !== undefined) lines.push(note);

  const obstacle = state.obstacles.find((o) => o.hex === hex);
  if (obstacle !== undefined) {
    lines.push(`${titleCase(String(obstacle.kind).replace(/_/g, " "))} stands here.`);
  }
  // The three soak layers are absent on a board that never had them, which is
  // why each is checked for existence and not just for a set byte.
  if (state.wet?.[hex] === 1) lines.push("This hex is wet.");
  if (state.fire?.[hex] === 1) lines.push("This hex is burning.");
  if (state.poison?.[hex] === 1) lines.push("This hex is poisoned.");

  const occupant = occupantSlot(state, hex);
  if (occupant !== null) {
    const def = content.creatures.get(state.stacks.def[occupant]! as CreatureDefId);
    if (def !== undefined) lines.push(`${def.namePlural} are standing here.`);
  }
  if (state.siege?.active === true) {
    lines.push(`Siege: fort level ${String(state.siege.fortLevel)}, gate ${state.siege.gateOpen ? "open" : "shut"}.`);
  }
  if (lines.length === 0) lines.push("Open ground.");

  return {
    title: titleCase(state.terrain),
    subtitle: `Hex ${String(hexCol(hex))}, ${String(hexRow(hex))}`,
    icon: null,
    lines,
    rows: [],
    footer: null,
  };
}

/** Which stack is on `hex`, or null. Two-hex creatures cover their tail too. */
export function occupantSlot(state: BattleState, hex: number): number | null {
  const st = state.stacks;
  for (let i = 0; i < st.n; i++) {
    if (st.count[i]! <= 0) continue;
    if (st.hex[i]! === hex) return i;
  }
  return null;
}
